import { Component, Input, OnChanges, OnInit, SimpleChanges, inject, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import {
  GrowthJourneyService,
  GrowthSwotArtifactSummary,
  GrowthSwotLinkedArtifacts
} from './growth-journey.service';
import { parseRichSituationAssessmentPayload } from './growth-strategy-artifact-content.util';
import {
  isStrategyArtifactApproved,
  normalizeStrategyArtifactStatus
} from './growth-status.util';

interface SituationAssessmentPreview {
  marketContext?: string;
  competitiveLandscape?: string;
  customerSegmentsInsight?: string;
  channelReadiness?: string;
  opportunityHorizon?: string;
  riskFlags: string[];
}

@Component({
  standalone: true,
  selector: 'ain-growth-discovery-situation-assessment-panel',
  imports: [RouterLink, InfoHintComponent, AinStateComponent, ButtonModule, TagModule],
  template: `
    <section id="situation-assessment" class="situation-panel">
      <div class="section-title">
        <h3>{{ t('growth.journey.situation.panel.title') }}</h3>
      </div>

      <ain-info-hint
        [titleKey]="'growth.journey.situation.help.title'"
        [descriptionKey]="'growth.journey.situation.help.description'"
        [bulletKeys]="[
          'growth.journey.situation.help.bullets.review',
          'growth.journey.situation.help.bullets.approval'
        ]" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else {
        @if (situationArtifact(); as artifact) {
          <div class="meta-row">
            <p-tag
              [value]="artifactStatusLabel(artifact.status)"
              [severity]="artifactStatusSeverity(artifact.status)" />
          </div>

          <div class="artifact-links">
            <a
              pButton
              severity="secondary"
              [routerLink]="['/growth/agents/strategy-artifacts', artifact.id]"
              [label]="t('growth.journey.situation.artifacts.review')"></a>
          </div>

          @if (preview(); as item) {
            <dl class="preview-list">
              @if (item.marketContext) {
                <div class="preview-item">
                  <dt>{{ t('growth.strategy.situation.marketContext') }}</dt>
                  <dd>{{ item.marketContext }}</dd>
                </div>
              }
              @if (item.competitiveLandscape) {
                <div class="preview-item">
                  <dt>{{ t('growth.strategy.situation.competitiveLandscape') }}</dt>
                  <dd>{{ item.competitiveLandscape }}</dd>
                </div>
              }
              @if (item.customerSegmentsInsight) {
                <div class="preview-item">
                  <dt>{{ t('growth.strategy.situation.customerSegmentsInsight') }}</dt>
                  <dd>{{ item.customerSegmentsInsight }}</dd>
                </div>
              }
              @if (item.channelReadiness) {
                <div class="preview-item">
                  <dt>{{ t('growth.strategy.situation.channelReadiness') }}</dt>
                  <dd>{{ item.channelReadiness }}</dd>
                </div>
              }
              @if (item.opportunityHorizon) {
                <div class="preview-item">
                  <dt>{{ t('growth.strategy.situation.opportunityHorizon') }}</dt>
                  <dd>{{ item.opportunityHorizon }}</dd>
                </div>
              }
              @if (item.riskFlags.length > 0) {
                <div class="preview-item">
                  <dt>{{ t('growth.strategy.situation.riskFlags') }}</dt>
                  <dd>
                    <ul>
                      @for (flag of item.riskFlags; track $index) {
                        <li>{{ flag }}</li>
                      }
                    </ul>
                  </dd>
                </div>
              }
            </dl>
          }

          @if (!isApproved(artifact.status)) {
            <p class="hint-text">{{ t('growth.journey.situation.hints.approvalRequired') }}</p>
          }
        } @else if (!swotApproved()) {
          <p class="hint-text">{{ t('growth.journey.situation.errors.swotNotApproved') }}</p>
        } @else {
          <p class="hint-text">{{ t('growth.journey.situation.errors.missingArtifact') }}</p>
        }
      }
    </section>
  `,
  styles: [`
    .situation-panel { border-top: 1px solid #e5e7eb; padding-top: 12px; display: grid; gap: 12px; }
    .section-title h3 { margin: 0; }
    .meta-row, .artifact-links { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .preview-list { margin: 0; display: grid; gap: 10px; }
    .preview-item { display: grid; gap: 4px; }
    .preview-item dt { font-weight: 600; color: #374151; }
    .preview-item dd { margin: 0; color: #4b5563; white-space: pre-wrap; }
    .preview-item ul { margin: 0; padding-inline-start: 18px; }
    .hint-text { margin: 0; color: #6b7280; }
  `]
})
export class GrowthDiscoverySituationAssessmentPanelComponent implements OnInit, OnChanges {
  @Input({ required: true }) discoveryId!: string;
  @Input() latestSituationAssessmentArtifactId: string | null | undefined = null;

  private readonly journey = inject(GrowthJourneyService);
  private readonly i18n = inject(LocalizationService);

  readonly loading = signal(false);
  readonly artifacts = signal<GrowthSwotLinkedArtifacts | null>(null);
  readonly situationArtifact = signal<GrowthSwotArtifactSummary | null>(null);
  readonly preview = signal<SituationAssessmentPreview | null>(null);

  ngOnInit(): void {
    this.loadContext();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['discoveryId'] && !changes['discoveryId'].firstChange) {
      this.loadContext();
    }

    if (changes['latestSituationAssessmentArtifactId'] && !changes['latestSituationAssessmentArtifactId'].firstChange) {
      this.loadContext();
    }
  }

  swotApproved(): boolean {
    const swot = this.artifacts()?.swot;
    return !!swot && isStrategyArtifactApproved(swot.status);
  }

  isApproved(status: unknown): boolean {
    return isStrategyArtifactApproved(status);
  }

  artifactStatusLabel(status: unknown): string {
    const normalized = normalizeStrategyArtifactStatus(status);
    return this.t(`growth.journey.marketingPlan.status.${normalized.charAt(0).toLowerCase()}${normalized.slice(1)}`);
  }

  artifactStatusSeverity(status: unknown): 'secondary' | 'info' | 'success' | 'warn' | 'danger' | 'contrast' {
    const normalized = normalizeStrategyArtifactStatus(status);
    if (normalized === 'Approved') return 'success';
    if (normalized === 'InReview') return 'info';
    if (normalized === 'Archived') return 'secondary';
    return 'warn';
  }

  t(key: string): string {
    return this.i18n.translate(key);
  }

  private loadContext(): void {
    this.loading.set(true);

    this.journey.getSwotArtifacts(this.discoveryId).subscribe({
      next: linked => {
        this.artifacts.set(linked);
        const artifact = this.resolveSituationArtifact(linked);
        this.situationArtifact.set(artifact);
        this.preview.set(this.buildPreview(artifact));
        this.loading.set(false);
      },
      error: () => {
        this.artifacts.set(null);
        this.situationArtifact.set(null);
        this.preview.set(null);
        this.loading.set(false);
      }
    });
  }

  private resolveSituationArtifact(linked: GrowthSwotLinkedArtifacts): GrowthSwotArtifactSummary | null {
    if (this.latestSituationAssessmentArtifactId) {
      if (linked.situationAssessment?.id === this.latestSituationAssessmentArtifactId) {
        return linked.situationAssessment;
      }
    }

    return linked.situationAssessment ?? null;
  }

  private buildPreview(artifact: GrowthSwotArtifactSummary | null): SituationAssessmentPreview | null {
    if (!artifact?.artifactJson) {
      return null;
    }

    const parsed = parseRichSituationAssessmentPayload(artifact.artifactJson);
    if (!parsed) {
      return null;
    }

    return {
      marketContext: parsed.marketContext,
      competitiveLandscape: parsed.competitiveLandscape,
      customerSegmentsInsight: parsed.customerSegmentsInsight,
      channelReadiness: parsed.channelReadiness,
      opportunityHorizon: parsed.opportunityHorizon,
      riskFlags: parsed.riskFlags ?? []
    };
  }
}
